import { I18n } from "@/i18n/I18n";
import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { useProjectStore } from "@/frontend/store/ProjectStore";
import { useMilestoneStore } from "@/frontend/store/MilestoneStore";
import { useShallow } from "zustand/react/shallow";
import { useGlobalStore } from "@/frontend/store/GlobalStore";
import { Button, Modal, Form } from "react-bootstrap";
import { Milestone } from "@/backend/models/milestone";
import { PencilSquare } from "@/frontend/SVGs/PencilSquare";
import { ButtonParent } from "../buttons/ButtonParent";
import { DeleteButton } from "../buttons/DeleteButton";
import { TruncatedText } from "../cards/TruncatedText";
import { SearchList } from "../SearchList";
import { parseLocalDateString } from "@/frontend/utils/formateDate";
import { NotFound } from "@/frontend/pages/NotFound";
import { LoadingSpinner } from "../LoadingSpinner";

export function ProjectMilestonesList() {
  const projectId = useParams().projectId;
  const project = useProjectStore(useShallow(state => state.projects.find(p => p.id === projectId)));
  const userClass = useGlobalStore(state => state.userClass);

  const milestones = useMilestoneStore(state => state.milestones);
  const fetchMilestonesByProjectId = useMilestoneStore(state => state.fetchMilestonesByProjectId);
  const createMilestone = useMilestoneStore(state => state.createMilestone);
  const resetMilestoneStore = useMilestoneStore(state => state.reset);

  const [milestoneList, setMilestoneList] = useState<Milestone[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
 
  useEffect(() => {
    if (projectId) {
      setLoading(true);
      void fetchMilestonesByProjectId(projectId).finally(() => {
        setLoading(false);
      });
    }

    return () => {
      resetMilestoneStore();
    };
  }, [projectId]);

  useEffect(() => {
    setMilestoneList(milestones);
  }, [milestones]);

  if (!projectId) {
    return <NotFound />;
  };

  const canManageMilestones = userClass?.getOrgId() === project?.propelOrgId;

  return (  
    <div className="p-3">
      <div className="pb-3">
        <div className="d-flex gap-2 align-items-center">
          <h1 className="border-end pe-2">{I18n.get('milestones')}</h1>
          { canManageMilestones &&
            <Button variant="primary" onClick={() => setShowCreateModal(true)}>
              {I18n.get('createModel', { model: I18n.get('milestoneUppercase') })}
            </Button>
          }
        </div>
      </div>
      <div>
          <SearchList
            items={milestones}
            setItems={setMilestoneList}
            modelLabel={I18n.get('milestoneUppercase')}
          />
          <div className="mt-3">
            <table className="table table-hover">
              <thead>
                <tr>
                  <th>{I18n.get('name')}</th>
                  <th>{I18n.get('description')}</th>
                  <th>{I18n.get('dueDate')}</th>
                  {canManageMilestones && <th className="text-end">{I18n.get('actions')}</th>}
                </tr>
              </thead>
              <tbody>
                <MilestonesTable milestones={milestoneList} canManageMilestones={canManageMilestones} loading={loading} />
              </tbody>
            </table>
          </div>
        </div>
        {showCreateModal && <MilestoneModal
          onHide={() => setShowCreateModal(false)}
          headerText={I18n.get('createModel', { model: I18n.get('milestoneUppercase') })}
          onConfirm={(data) => createMilestone({ ...data, projectId })}
        />}
    </div>
  );
}

function MilestonesTable(props: { milestones: Milestone[], canManageMilestones: boolean, loading: boolean }) {
  const { milestones, canManageMilestones, loading } = props;

  if(loading) {
    return (
      <tr>
        <td colSpan={4} className="text-center py-4">
          <LoadingSpinner />
        </td>
      </tr>
    );
  }

  if(milestones.length === 0) {
    return (
      <tr>
        <td colSpan={4} className="text-center py-4">
          <p className="text-muted">{I18n.get('noMilestones')}</p>
        </td>
      </tr>
    );
  }

  return milestones.map((milestone) => (
    <MilestoneRow key={milestone.id} milestone={milestone} canManageMilestones={canManageMilestones} />
  ));
}

function MilestoneRow(props: { milestone: Milestone, canManageMilestones: boolean }) {
  const { milestone, canManageMilestones } = props;

  const deleteMilestone = useMilestoneStore(state => state.deleteMilestone);
  const updateMilestone = useMilestoneStore(state => state.updateMilestone);

  const [showEditModal, setShowEditModal] = useState(false);

  return (
    <tr>
      <td>{milestone.name}</td>
      <td><TruncatedText text={milestone.description ?? ''} maxLength={100} showButton={false} /></td>
      <td>{milestone.dueDate ? parseLocalDateString(milestone.dueDate) : ''}</td>
      {canManageMilestones && (
        <td className="text-end">
          <div className="d-flex justify-content-end gap-2">
            <ButtonParent
              variant="secondary"
              onClick={() => setShowEditModal(true)}
              title={I18n.get('editModel', { model: I18n.get('milestoneUppercase'), title: milestone.name })}
            >
              <PencilSquare />
            </ButtonParent>
            <DeleteButton
              model={I18n.get('milestoneUppercase')}
              title={milestone.name}
              onDelete={() => deleteMilestone(milestone.id)}
            />
          </div>
        </td>
      )}
      {showEditModal && <MilestoneModal 
        onHide={() => setShowEditModal(false)} 
        milestone={milestone} 
        headerText={I18n.get('editModel', { model: I18n.get('milestoneUppercase'), title: milestone.name })} 
        onConfirm={(data) => updateMilestone(milestone.id, data)}
      />}
    </tr>
  );
};

function MilestoneModal(props: { onHide: () => void, milestone?: Milestone, headerText: string, onConfirm: (data: { name: string, description: string, dueDate: string | undefined }) => Promise<unknown> }) {
  const { onHide, milestone, headerText, onConfirm } = props;

  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [dueDate, setDueDate] = useState('');
  const [nameError, setNameError] = useState("");

  useEffect(() => {
    if (milestone) {
      setName(milestone.name);
      setDescription(milestone.description ?? "");
      setDueDate(milestone.dueDate ? milestone.dueDate.split('T')[0] : '');
    }
  }, [milestone]);

  const handleEdit = async () => {
    if (!name) {
      setNameError(I18n.get('requiredFieldError', { field: I18n.get('name') }));
      return;
    }

    await onConfirm({ name, description, dueDate });
    onClose();
  };

  const onClose = () => {
    setName("");
    setDescription("");
    setDueDate('');
    setNameError("");
    onHide();
  };

  return (
    <Modal show onHide={onClose}>
      <Modal.Header closeButton>
        <Modal.Title>{headerText}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form>
          <Form.Group className="mb-3">
            <Form.Label htmlFor="edit-milestone-name">{I18n.get('name')}</Form.Label>
            <Form.Control
              id="edit-milestone-name"
              type="text"
              value={name}
              placeholder={I18n.get('enterField', { field: I18n.get('name') })}
              isInvalid={!!nameError}
              onChange={(e) => { setName(e.target.value); setNameError(""); }}
            />
            <Form.Control.Feedback type="invalid">{nameError}</Form.Control.Feedback>
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label htmlFor="edit-milestone-description">{`${I18n.get('description')} (${I18n.get('optional')})`}</Form.Label>
            <Form.Control
              id="edit-milestone-description"
              as="textarea"
              rows={3}
              value={description}
              placeholder={I18n.get('enterField', { field: I18n.get('description') })}
              onChange={(e) => setDescription(e.target.value)}
            />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label htmlFor="milestone-due-date">{I18n.get('dueDate')}</Form.Label>
            <Form.Control
              id="milestone-due-date"
              type="date"
              value={dueDate}
              placeholder={I18n.get('enterField', { field: I18n.get('dueDate') })}
              onChange={(e) => setDueDate(e.target.value)}
            />
          </Form.Group>
        </Form>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onClose}>
          {I18n.get('cancel')}
        </Button>
        <Button variant="primary" onClick={() => void handleEdit()}>
          {I18n.get('confirm')}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
